import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getBlog } from '../services/api';

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const data = await getBlog(slug);
        setBlog(data);
      } catch (error) {
        console.error('Error fetching blog:', error);
        setError('Blog post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-teal)]"></div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center">
        <h1 className="text-2xl font-bold text-gray-600 mb-4">Blog post not found</h1>
        <Link to="/blog">
          <Button className="btn-gold">
            <ArrowLeft size={16} className="mr-2" />
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      {blog.image_url && (
        <div className="relative h-[400px] overflow-hidden">
          <img
            src={blog.image_url}
            alt={blog.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        </div>
      )}

      {/* Content */}
      <article className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Link to="/blog" className="inline-flex items-center text-[var(--primary-teal)] hover:underline mb-6">
            <ArrowLeft size={16} className="mr-2" />
            Back to Blog
          </Link>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {blog.tags.map((tag, index) => (
                <span
                  key={index}
                  className="text-sm px-3 py-1 bg-[var(--gold-accent)]/10 text-[var(--gold-accent)] rounded-full flex items-center"
                >
                  <Tag size={12} className="mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--primary-teal)] mb-4" data-testid="blog-detail-title">
            {blog.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center text-gray-500 mb-8 pb-8 border-b">
            <User size={16} className="mr-2" />
            <span className="mr-6">{blog.author}</span>
            <Calendar size={16} className="mr-2" />
            <span>{formatDate(blog.created_at)}</span>
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: blog.content.replace(/\n/g, '<br/>') }}
          />
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;
